import {
  Button,
  Divider,
  Form,
  Input,
  InputNumber,
  Select,
  Typography,
  notification,
} from '@pankod/refine-antd';
import { useCan } from '@pankod/refine-core';
import { ISale } from 'interfaces';
import { useEffect, useState } from 'react';
import { IReturnProduct } from 'reducers/product-return-reducer';
import { IVariant, getVariant } from 'services/variant';

type AddReturnProductProps = {
  isInitialDataLoading: boolean;
  selectedVariant: IVariant | null;
  options: ISale['salesProductsCopy'];
  returnProducts: IReturnProduct[];
  onSubmit: (product: IReturnProduct) => void;
  invoiceDiscount: number
};

const { Option } = Select;

export const AddReturnProduct: React.FC<AddReturnProductProps> = (props) => {
  const [isLoading, setIsLoading] = useState<boolean>(props.isInitialDataLoading);
  const [selectedVariant, setSelectedVariant] = useState<IVariant | null>(null);
  const [form] = Form.useForm();
  const { data: readPurchasePrice } = useCan({
    resource: 'purchase_price',
    action: 'read',
  });

  useEffect(() => {
    setIsLoading(props.isInitialDataLoading);
    setSelectedVariant(props.selectedVariant);
  }, [props.isInitialDataLoading, props.selectedVariant]);

  useEffect(() => {
    const saleProduct = props.options.find(
      (saleProduct) => saleProduct.productVariant.id === selectedVariant?.productVariantId
    );

    form.setFields([
      { name: 'salesProduct', value: saleProduct?.id },
      {
        name: 'variant',
        value: selectedVariant?.productVariantId,
      },
      { name: 'unitPrice', value: saleProduct?.unitPrice },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariant]);

  const onSelectProductVariant = async (id: string) => {
    form.resetFields(['returnQuantity', 'subTotal', 'lossAmount']);

    setIsLoading(true);
    try {
      const variant = await getVariant(id);
      setSelectedVariant(variant);
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
    setIsLoading(false);
  };

  const variantOptionElements = props.options.map((saleProduct) => {
    return (
      <Option key={saleProduct.productVariant.id} value={saleProduct.productVariant.id}>
        {`${saleProduct.productVariant.sku} - ${saleProduct.productVariant.name} (${saleProduct.quantity})`}
      </Option>
    );
  });

  const attributes: {
    attributeId: string;
    attributeName: string;
    attributeType: 'text' | 'float' | 'boolean' | 'dropdown';
    value: string;
  }[] = [];
  const templates = selectedVariant?.productTemplates || [];
  templates.forEach((template) => {
    const { productTemplateAttributes } = template;
    attributes.push(...productTemplateAttributes);
  });

  const attributeElements = attributes.map((attribute) => (
    <div
      key={attribute.attributeId}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
    >
      <Typography.Text strong>{attribute.attributeName.toUpperCase()} : </Typography.Text>
      <Typography.Text>{attribute.value}</Typography.Text>
    </div>
  ));

  const customFields = selectedVariant?.customFields ?? [];

  const customFieldElements = customFields.map((field, idx) => {
    const key = Object.keys(field)[0];
    const value = field[key];

    return (
      <div
        key={idx}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Typography.Text strong>{key.toUpperCase()} : </Typography.Text>
        <Typography.Text>{value}</Typography.Text>
      </div>
    );
  });

  const getDiscountPerUnit = () => {
    if (!selectedVariant) return 0;

    const saleProduct = props.options.find(
      (sp) => sp.productVariant.id === selectedVariant.productVariantId
    );

    if (!saleProduct) return 0;

    const discountPerUnit = (saleProduct.discountCost || 0) / (saleProduct.quantity || 1);
    return discountPerUnit;
  };

  const onReturnQuantityChange = (value: number | null) => {
    const unitPrice = form.getFieldValue('unitPrice') || 0;
    const discountPerUnit = getDiscountPerUnit();
    const discountedUnitPrice = +(unitPrice - discountPerUnit).toFixed(2);
    const subTotal = value ? value * discountedUnitPrice : 0;
    form.setFieldsValue({ subTotal });
  };

  interface FormValues {
    salesProduct: string;
    product: string;
    unitPrice: number;
    returnQuantity: number;
    subTotal: number;
    lossAmount: number;
  }

  const onFinish = (values: FormValues) => {
    if (!selectedVariant) return;

    const saleProduct = props.options.find(
      (saleProduct) => saleProduct.productVariant.id === selectedVariant.productVariantId
    );
    if (!saleProduct) return;

    const data = {
      ...selectedVariant,
      unitPrice: Number.parseFloat((values.unitPrice - getDiscountPerUnit()).toFixed(2)),
      quantity: values.returnQuantity,
      lossAmount: Number.parseFloat((values.lossAmount || 0).toFixed(2)),
      salesProduct: values.salesProduct,
    };

    const variantExists = !!props.returnProducts.find(
      (product) => product.productVariantId === selectedVariant.productVariantId
    );
    if (variantExists) {
      form.setFields([
        {
          name: 'variant',
          errors: [`Product is already added.`],
        },
      ]);
      return;
    }

    if (data.quantity === 0) {
      form.setFields([
        {
          name: 'returnQuantity',
          errors: [`Quantity needs to be greater than 0`],
        },
      ]);
      return;
    }

    if (data.quantity > saleProduct.quantity) {
      form.setFields([
        {
          name: 'returnQuantity',
          errors: [`Quantity needs to be between 1 and ${saleProduct.quantity}`],
        },
      ]);
      return;
    }

    props.onSubmit(data);
    setSelectedVariant(null);
    form.resetFields();
  };

  return (
    <Form
      layout="vertical"
      initialValues={{ isActive: true, lossAmount: 0 }}
      form={form}
      onFinish={onFinish}
    >
      <Form.Item label="Sales Product ID" name="salesProduct" style={{ display: 'none' }}>
        <Input disabled />
      </Form.Item>
      <Form.Item
        label="Product"
        name="variant"
        rules={[{ required: true, message: 'Please select a product' }]}
      >
        <Select
          showSearch
          style={{ width: '100%' }}
          placeholder="Select a product"
          disabled={props.isInitialDataLoading || isLoading}
          loading={props.isInitialDataLoading || isLoading}
          onSelect={onSelectProductVariant}
          filterOption={(input, option: any) =>
            option!.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {variantOptionElements}
        </Select>
      </Form.Item>

      {selectedVariant && (
        <>
          <Divider />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography.Text strong>PARENT PRODUCT:</Typography.Text>
              <Typography.Text code>{selectedVariant.productName}</Typography.Text>
            </div>

            {attributeElements}
            {customFieldElements}

            {readPurchasePrice?.can && (
              <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <Typography.Text strong>PURCHASE PRICE:</Typography.Text>
                <Typography.Text>{selectedVariant.purchasePrice}</Typography.Text>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography.Text strong>SELLING PRICE:</Typography.Text>
              <Typography.Text>{selectedVariant.sellingPrice}</Typography.Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography.Text strong style={{ color: '#808080' }}>
                (**Discount per unit: {getDiscountPerUnit().toFixed(2)} | Invoice Discount: {props.invoiceDiscount})
              </Typography.Text>
            </div>
          </div>
          <Divider />
        </>
      )}

      <Form.Item label="Unit Price" name="unitPrice" style={{ display: 'none' }}>
        <Input disabled />
      </Form.Item>
      <Form.Item label="Return Quantity" name="returnQuantity" rules={[{ required: true }]}>
        <InputNumber style={{ width: '100%' }} onChange={onReturnQuantityChange} />
      </Form.Item>
      <Form.Item label="Value" name="subTotal">
        <InputNumber disabled style={{ width: '100%' }} min={0} />
      </Form.Item>

      <Form.Item wrapperCol={{ span: 4, offset: 20 }}>
        <Button type="primary" htmlType="submit">
          Save
        </Button>
      </Form.Item>
    </Form>
  );
};
